import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X, ChevronDown, ChevronUp, Save, Pencil, Info,
  Activity, AlertCircle, Bandage, ShieldAlert, Sparkles, Wrench, FileText,
} from 'lucide-react';
import { cn } from '@/utils/cn';
import { formatTooth } from '@/utils/toothNotation';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Textarea';
import type {
  ToothData,
  ToothNotation,
  Surface,
  ToothStatusType,
  SurfaceStatus,
  RootZone,
  RootCondition,
  RootZoneStatus,
} from './types';
import { getSurfaceConditions } from './types';

// ── Condition meta ─────────────────────────────────────────────────────────────

interface ConditionMeta {
  label: string;
  fill: string;
  stroke: string;
  textColor: string;
}

const CROWN_CONDITIONS: Record<ToothStatusType, ConditionMeta> = {
  healthy:            { label: 'Rask',               fill: '#f0fdf4', stroke: '#28a056', textColor: '#15803d' },
  caries:             { label: 'Caries',             fill: '#fde8e8', stroke: '#e05252', textColor: '#b91c1c' },
  filled:             { label: 'Fyldning',           fill: '#dce8fc', stroke: '#4b8fe8', textColor: '#1d4ed8' },
  crown:              { label: 'Krone',              fill: '#fef4d3', stroke: '#d4a017', textColor: '#854d0e' },
  missing:            { label: 'Mangler',            fill: '#f5f4f1', stroke: '#a8a29e', textColor: '#57534e' },
  implant:            { label: 'Implantat',          fill: '#eae5fd', stroke: '#8b6ce8', textColor: '#5b21b6' },
  root_canal:         { label: 'Rodbehandlet',       fill: '#fff8f0', stroke: '#e07a28', textColor: '#c2410c' },
  bridge:             { label: 'Bro',                fill: '#e4f9f5', stroke: '#1ea898', textColor: '#0f766e' },
  extraction_planned: { label: 'Planl. udtræk',      fill: '#fbd9d9', stroke: '#c83030', textColor: '#991b1b' },
  fracture:           { label: 'Fraktur',            fill: '#fde8e8', stroke: '#b52a2a', textColor: '#7f1d1d' },
  periapical_abscess: { label: 'Periap. absces',     fill: '#fce8fd', stroke: '#b838c8', textColor: '#7e22ce' },
  veneer:             { label: 'Finér',              fill: '#e0f9fc', stroke: '#0fa8bc', textColor: '#0e7490' },
  post_and_core:      { label: 'Stift/opbygning',   fill: '#e4faea', stroke: '#28a056', textColor: '#166534' },
};

const ROOT_CONDITIONS: Record<RootCondition, ConditionMeta> = {
  healthy:            { label: 'Rask',              fill: '#f0fdf4', stroke: '#28a056', textColor: '#15803d' },
  root_canal_treated: { label: 'Rodbehandlet',      fill: '#fff8f0', stroke: '#e07a28', textColor: '#c2410c' },
  infected:           { label: 'Inficeret',         fill: '#fde8e8', stroke: '#e05252', textColor: '#b91c1c' },
  calcification:      { label: 'Forkalkning',       fill: '#fef4d3', stroke: '#d4a017', textColor: '#854d0e' },
  resorption:         { label: 'Resorption',        fill: '#fce8fd', stroke: '#b838c8', textColor: '#7e22ce' },
  fracture:           { label: 'Fraktur',           fill: '#fde8e8', stroke: '#b52a2a', textColor: '#7f1d1d' },
  perforation:        { label: 'Perforering',       fill: '#fde8f0', stroke: '#c8305a', textColor: '#9f1239' },
  post:               { label: 'Stift',             fill: '#f5f4f1', stroke: '#a8a29e', textColor: '#57534e' },
};

const SURFACE_LABELS: Record<Surface, string> = {
  occlusal: 'Okklusal (O)',
  buccal:   'Buccal (B)',
  lingual:  'Lingual (L)',
  mesial:   'Mesial (M)',
  distal:   'Distal (D)',
};

const ROOT_ZONE_LABELS: Record<RootZone, string> = {
  cervical: 'Cervikal',
  mid:      'Midtre',
  apex:     'Apex',
};

// Surface conditions available in the surface picker
const SURFACE_CONDITION_KEYS: ToothStatusType[] = [
  'healthy', 'caries', 'filled', 'crown', 'bridge', 'fracture', 'veneer', 'post_and_core',
];

const ROOT_CONDITION_KEYS: RootCondition[] = [
  'healthy', 'root_canal_treated', 'infected', 'calcification',
  'resorption', 'fracture', 'perforation', 'post',
];

// ── Category definitions for Tandstatus ──────────────────────────────────────

interface StatusCategory {
  label: string;
  hint?: string;
  icon: React.ReactNode;
  keys: ToothStatusType[];
}

const TANDSTATUS_CATEGORIES: StatusCategory[] = [
  {
    label: 'Tilstand',
    icon: <Activity className="h-3.5 w-3.5" />,
    keys: ['healthy', 'missing'],
  },
  {
    label: 'Patologi',
    icon: <AlertCircle className="h-3.5 w-3.5" />,
    keys: ['fracture', 'periapical_abscess', 'extraction_planned'],
  },
  {
    label: 'Endodonti',
    icon: <ShieldAlert className="h-3.5 w-3.5" />,
    keys: ['root_canal', 'post_and_core'],
  },
  {
    label: 'Protetik',
    icon: <Sparkles className="h-3.5 w-3.5" />,
    keys: ['crown', 'bridge', 'veneer'],
  },
  {
    label: 'Implantat',
    icon: <Wrench className="h-3.5 w-3.5" />,
    keys: ['implant'],
  },
];

// ── Surface condition categories ──────────────────────────────────────────────

interface SurfaceCategory {
  label: string;
  icon: React.ReactNode;
  keys: ToothStatusType[];
}

const SURFACE_CATEGORIES: SurfaceCategory[] = [
  {
    label: 'Tilstand',
    icon: <Activity className="h-3.5 w-3.5" />,
    keys: ['healthy'],
  },
  {
    label: 'Diagnose',
    icon: <AlertCircle className="h-3.5 w-3.5" />,
    keys: ['caries', 'fracture'],
  },
  {
    label: 'Restaurering',
    icon: <Bandage className="h-3.5 w-3.5" />,
    keys: ['filled', 'crown', 'bridge', 'veneer', 'post_and_core'],
  },
];

// ── Surface diagram ───────────────────────────────────────────────────────────

interface SurfaceDiagramProps {
  surfaces: Partial<Record<Surface, SurfaceStatus | { status?: string; material?: string }>>;
  activeSurfaces: Surface[];
  onSurfaceClick: (s: Surface) => void;
  isEditing: boolean;
}

function SurfaceDiagram({ surfaces, activeSurfaces, onSurfaceClick, isEditing }: SurfaceDiagramProps) {
  const getSurfaceStyle = (surface: Surface) => {
    const conditions = getSurfaceConditions(surfaces[surface]);
    const primary = conditions[0];
    if (!primary || primary === 'healthy') return { fill: '#fffefb', stroke: '#e7e5e4' };
    const meta = CROWN_CONDITIONS[primary];
    return { fill: meta.fill, stroke: meta.stroke };
  };

  const surfaceClass = (surface: Surface) =>
    cn(
      'transition-all duration-150',
      isEditing && 'cursor-pointer',
      activeSurfaces.includes(surface) && 'brightness-90',
      isEditing && !activeSurfaces.includes(surface) && 'hover:brightness-95',
    );

  const buccal   = getSurfaceStyle('buccal');
  const lingual  = getSurfaceStyle('lingual');
  const mesial   = getSurfaceStyle('mesial');
  const distal   = getSurfaceStyle('distal');
  const occlusal = getSurfaceStyle('occlusal');

  const ring = (surface: Surface) =>
    activeSurfaces.includes(surface)
      ? { stroke: '#6366f1', strokeWidth: 2.5 }
      : {};

  return (
    <svg viewBox="0 0 160 160" className="w-full max-w-[200px] mx-auto">
      {/* Buccal (top) */}
      <path
        d="M 20,10 L 140,10 L 116,52 L 44,52 Z"
        fill={buccal.fill} stroke={buccal.stroke} strokeWidth="1.5"
        className={surfaceClass('buccal')}
        onClick={() => isEditing && onSurfaceClick('buccal')}
        {...ring('buccal')}
      />
      <text x="80" y="35" textAnchor="middle" fontSize="12" fill="#78716c" className="select-none pointer-events-none font-semibold">B</text>

      {/* Lingual (bottom) */}
      <path
        d="M 44,108 L 116,108 L 140,150 L 20,150 Z"
        fill={lingual.fill} stroke={lingual.stroke} strokeWidth="1.5"
        className={surfaceClass('lingual')}
        onClick={() => isEditing && onSurfaceClick('lingual')}
        {...ring('lingual')}
      />
      <text x="80" y="133" textAnchor="middle" fontSize="12" fill="#78716c" className="select-none pointer-events-none font-semibold">L</text>

      {/* Mesial (left) */}
      <path
        d="M 10,20 L 44,52 L 44,108 L 10,140 Z"
        fill={mesial.fill} stroke={mesial.stroke} strokeWidth="1.5"
        className={surfaceClass('mesial')}
        onClick={() => isEditing && onSurfaceClick('mesial')}
        {...ring('mesial')}
      />
      <text x="24" y="83" textAnchor="middle" fontSize="12" fill="#78716c" className="select-none pointer-events-none font-semibold">M</text>

      {/* Distal (right) */}
      <path
        d="M 116,52 L 150,20 L 150,140 L 116,108 Z"
        fill={distal.fill} stroke={distal.stroke} strokeWidth="1.5"
        className={surfaceClass('distal')}
        onClick={() => isEditing && onSurfaceClick('distal')}
        {...ring('distal')}
      />
      <text x="136" y="83" textAnchor="middle" fontSize="12" fill="#78716c" className="select-none pointer-events-none font-semibold">D</text>

      {/* Occlusal (center) */}
      <rect
        x="44" y="52" width="72" height="56" rx="6"
        fill={occlusal.fill} stroke={occlusal.stroke} strokeWidth="1.5"
        className={surfaceClass('occlusal')}
        onClick={() => isEditing && onSurfaceClick('occlusal')}
        {...ring('occlusal')}
      />
      <text x="80" y="83" textAnchor="middle" fontSize="12" fill="#78716c" className="select-none pointer-events-none font-semibold">O</text>
    </svg>
  );
}

// ── Root zone diagram ─────────────────────────────────────────────────────────

interface RootDiagramProps {
  rootZones: RootZoneStatus[];
  activeZone: RootZone | null;
  onZoneClick: (z: RootZone) => void;
  isEditing: boolean;
  isUpperTooth: boolean;
  hasRootCanal: boolean;
}

function RootDiagram({ rootZones, activeZone, onZoneClick, isEditing, isUpperTooth, hasRootCanal }: RootDiagramProps) {
  const getZoneStyle = (zone: RootZone) => {
    const zoneData = rootZones.find((rz) => rz.zone === zone);
    const primary = zoneData?.conditions.find((c) => c !== 'healthy');
    if (!primary) return { fill: '#f9f8f5', stroke: '#e7e5e4' };
    const meta = ROOT_CONDITIONS[primary];
    return { fill: meta.fill, stroke: meta.stroke };
  };

  const ringAttrs = (zone: RootZone) =>
    activeZone === zone ? { stroke: '#6366f1', strokeWidth: 3 } : {};

  const zoneClass = (zone: RootZone) =>
    cn(
      'transition-all duration-150',
      isEditing && 'cursor-pointer',
      activeZone === zone && 'brightness-90',
      isEditing && activeZone !== zone && 'hover:brightness-95',
    );

  const cervical = getZoneStyle('cervical');
  const mid      = getZoneStyle('mid');
  const apex     = getZoneStyle('apex');

  const hasAbscess = rootZones.find((rz) => rz.zone === 'apex')?.conditions.includes('infected');
  const hasPost    = rootZones.some((rz) => rz.conditions.includes('post'));
  const transform  = isUpperTooth ? 'translate(80,250) scale(1,-1) translate(-80,-250)' : undefined;

  return (
    <svg viewBox="0 0 160 260" className="w-full max-w-[80px] mx-auto">
      <g transform={transform}>
        <rect x="32" y="2" width="96" height="34" rx="7"
          fill="#f9f8f5" stroke="#e7e5e4" strokeWidth="1.5" />
        <text x="80" y="23" textAnchor="middle" fontSize="10" fill="#a8a29e" className="select-none">Krone</text>

        <path
          d="M 38,36 C 35,52 33,66 33,80 L 127,80 C 127,66 125,52 122,36 Z"
          fill={cervical.fill} stroke={cervical.stroke} strokeWidth="1.5"
          className={zoneClass('cervical')}
          onClick={() => isEditing && onZoneClick('cervical')}
          {...ringAttrs('cervical')}
        />
        <text x="80" y="62" textAnchor="middle" fontSize="9" fill="#a8a29e" className="select-none pointer-events-none">Cerv.</text>

        <path
          d="M 33,80 C 31,100 29,120 29,140 L 131,140 C 131,120 129,100 127,80 Z"
          fill={mid.fill} stroke={mid.stroke} strokeWidth="1.5"
          className={zoneClass('mid')}
          onClick={() => isEditing && onZoneClick('mid')}
          {...ringAttrs('mid')}
        />
        <text x="80" y="113" textAnchor="middle" fontSize="9" fill="#a8a29e" className="select-none pointer-events-none">Mid</text>

        <path
          d="M 29,140 C 27,162 24,184 28,206 C 34,228 60,246 80,250 C 100,246 126,228 132,206 C 136,184 133,162 131,140 Z"
          fill={apex.fill} stroke={apex.stroke} strokeWidth="1.5"
          className={zoneClass('apex')}
          onClick={() => isEditing && onZoneClick('apex')}
          {...ringAttrs('apex')}
        />
        <text x="80" y="196" textAnchor="middle" fontSize="9" fill="#a8a29e" className="select-none pointer-events-none">Apex</text>

        {hasRootCanal && (
          <line
            x1="80" y1="36" x2="80" y2="245"
            stroke="#e07a28" strokeWidth="2"
            strokeDasharray="5 3" strokeLinecap="round"
            opacity="0.65"
          />
        )}
        {hasPost && (
          <rect x="73" y="60" width="14" height="100" rx="3"
            fill="#a8a29e" opacity="0.5" />
        )}
        {hasAbscess && (
          <circle cx="80" cy="248" r="14"
            fill="none" stroke="#b838c8" strokeWidth="2"
            strokeDasharray="4 3" opacity="0.75" />
        )}
        <line x1="33" y1="80"  x2="127" y2="80"  stroke="#e7e5e4" strokeWidth="1" />
        <line x1="29" y1="140" x2="131" y2="140" stroke="#e7e5e4" strokeWidth="1" />
      </g>
    </svg>
  );
}

// ── Condition chip ────────────────────────────────────────────────────────────

function ConditionChip({
  label,
  fill,
  stroke,
  textColor,
  selected,
  partial = false,
  onClick,
}: ConditionMeta & { selected: boolean; partial?: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium border transition-all duration-100',
        selected
          ? 'ring-2 ring-offset-1 ring-indigo-500 scale-[1.03] shadow-sm'
          : partial
            ? 'ring-1 ring-offset-1 ring-indigo-300 opacity-75'
            : 'hover:scale-[1.02] hover:shadow-sm',
      )}
      style={{ background: fill, borderColor: stroke, color: textColor }}
      title={partial ? 'Gælder kun nogle valgte flader' : undefined}
    >
      <span
        className={cn('h-2 w-2 flex-shrink-0 rounded-full', partial && 'ring-1 ring-indigo-400')}
        style={{ background: stroke }}
      />
      {label}
    </button>
  );
}

// ── Collapsible section ───────────────────────────────────────────────────────

interface SectionProps {
  title: string;
  icon?: React.ReactNode;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  hint?: string;
}

function Section({ title, icon, open, onToggle, children, hint }: SectionProps) {
  return (
    <div className="rounded-xl border border-stone-200 bg-white shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between px-4 py-3 hover:bg-stone-50/60 transition-colors"
      >
        <div className="flex items-center gap-2">
          {icon && <span className="text-stone-400">{icon}</span>}
          <span className="text-[11px] font-bold uppercase tracking-wider text-stone-500">{title}</span>
          {hint && <span className="text-[10px] text-stone-400 normal-case font-normal tracking-normal">{hint}</span>}
        </div>
        {open
          ? <ChevronUp className="h-3.5 w-3.5 text-stone-400" />
          : <ChevronDown className="h-3.5 w-3.5 text-stone-400" />
        }
      </button>
      {open && <div className="px-4 pb-4 pt-1">{children}</div>}
    </div>
  );
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface ToothDetailPanelProps {
  tooth: ToothData | null;
  notation: ToothNotation;
  onSave?: (tooth: ToothData) => void;
  onClose?: () => void;
  readOnly?: boolean;
}

// ── Main component ─────────────────────────────────────────────────────────────

export function ToothDetailPanel({
  tooth,
  notation,
  onSave,
  onClose,
  readOnly = false,
}: ToothDetailPanelProps) {
  const { t } = useTranslation();

  const [isEditing, setIsEditing] = useState(false);
  const [editedStatuses, setEditedStatuses] = useState<ToothStatusType[]>([]);
  const [editedSurfaces, setEditedSurfaces] = useState<Partial<Record<Surface, SurfaceStatus>>>({});
  const [editedRootZones, setEditedRootZones] = useState<RootZoneStatus[]>([]);
  const [editedNotes, setEditedNotes] = useState('');
  const [activeSurfaces, setActiveSurfaces] = useState<Surface[]>([]);
  const [activeRootZone, setActiveRootZone] = useState<RootZone | null>(null);

  const [surfacesOpen, setSurfacesOpen] = useState(true);
  const [rootOpen, setRootOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(true);
  const [notesOpen, setNotesOpen] = useState(true);

  // ── Edit entry / exit ──────────────────────────────────────────────────────

  const startEditing = useCallback(() => {
    if (!tooth) return;
    setEditedStatuses([...tooth.status]);
    const normalizedSurfaces: Partial<Record<Surface, SurfaceStatus>> = {};
    for (const [key, val] of Object.entries(tooth.surfaces ?? {})) {
      normalizedSurfaces[key as Surface] = {
        conditions: getSurfaceConditions(val),
        material: (val as { material?: string })?.material,
      };
    }
    setEditedSurfaces(normalizedSurfaces);
    setEditedRootZones(
      tooth.root_zones
        ? [...tooth.root_zones.map((rz) => ({ ...rz, conditions: [...rz.conditions] }))]
        : [],
    );
    setEditedNotes(tooth.notes ?? '');
    setActiveSurfaces([]);
    setActiveRootZone(null);
    setIsEditing(true);
  }, [tooth]);

  const cancelEditing = useCallback(() => {
    setIsEditing(false);
    setActiveSurfaces([]);
    setActiveRootZone(null);
  }, []);

  // ── Condition toggles ──────────────────────────────────────────────────────

  const toggleToothCondition = (condition: ToothStatusType) => {
    setEditedStatuses((prev) => {
      if (condition === 'healthy') return ['healthy'];
      if (condition === 'missing') return ['missing'];
      const withoutHealthy = prev.filter((s) => s !== 'healthy' && s !== 'missing');
      return withoutHealthy.includes(condition)
        ? withoutHealthy.filter((s) => s !== condition).length > 0
          ? withoutHealthy.filter((s) => s !== condition)
          : ['healthy']
        : [...withoutHealthy, condition];
    });
  };

  const toggleSurfaceCondition = (surfaces: Surface[], condition: ToothStatusType) => {
    setEditedSurfaces((prev) => {
      const updated = { ...prev };
      for (const surface of surfaces) {
        const current = updated[surface] ?? { conditions: [] };
        const existing = current.conditions ?? [];
        let next: ToothStatusType[];
        if (condition === 'healthy') {
          next = [];
        } else {
          const allHaveIt = surfaces.every((s) => {
            const c = updated[s]?.conditions ?? [];
            return c.includes(condition);
          });
          next = allHaveIt
            ? existing.filter((c) => c !== condition)
            : [...existing.filter((c) => c !== 'healthy'), condition];
        }
        updated[surface] = { ...current, conditions: next };
      }
      return updated;
    });
  };

  const toggleRootCondition = (zone: RootZone, condition: RootCondition) => {
    setEditedRootZones((prev) => {
      const existing = prev.find((rz) => rz.zone === zone);
      const existingConditions = existing?.conditions ?? [];
      let next: RootCondition[];
      if (condition === 'healthy') {
        next = [];
      } else {
        next = existingConditions.includes(condition)
          ? existingConditions.filter((c) => c !== condition)
          : [...existingConditions.filter((c) => c !== 'healthy'), condition];
      }
      const without = prev.filter((rz) => rz.zone !== zone);
      if (next.length === 0) return without;
      return [...without, { zone, conditions: next }];
    });
  };

  // ── Save ───────────────────────────────────────────────────────────────────

  const handleSave = () => {
    if (!tooth || !onSave) return;
    onSave({
      ...tooth,
      status: editedStatuses.length > 0 ? editedStatuses : ['healthy'],
      surfaces: editedSurfaces,
      root_zones: editedRootZones,
      notes: editedNotes,
    });
    setIsEditing(false);
    setActiveSurfaces([]);
    setActiveRootZone(null);
  };

  const handleSurfaceClick = (surface: Surface) => {
    if (!isEditing) return;
    setActiveSurfaces((prev) =>
      prev.includes(surface) ? prev.filter((s) => s !== surface) : [...prev, surface],
    );
    setActiveRootZone(null);
  };

  const handleRootZoneClick = (zone: RootZone) => {
    if (!isEditing) return;
    setActiveRootZone((prev) => (prev === zone ? null : zone));
    setActiveSurfaces([]);
  };

  // ── Empty state ───────────────────────────────────────────────────────────

  if (!tooth) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[200px] rounded-2xl border border-dashed border-stone-300 dark:border-stone-600 bg-white dark:bg-stone-900 p-10 text-center">
        <Info className="mb-3 h-8 w-8 text-stone-300" />
        <p className="text-sm font-medium text-stone-500 dark:text-stone-400">
          Vælg en tand i diagrammet
        </p>
      </div>
    );
  }

  // ── Derived data ──────────────────────────────────────────────────────────

  const displayNumber    = formatTooth(tooth.tooth_fdi, notation);
  const currentStatuses  = isEditing ? editedStatuses : tooth.status;
  const currentSurfaces  = isEditing ? editedSurfaces : (tooth.surfaces as Partial<Record<Surface, SurfaceStatus>>);
  const currentRootZones = isEditing ? editedRootZones : (tooth.root_zones ?? []);

  const isUpperTooth = ['1', '2', '5', '6'].includes(tooth.tooth_fdi[0]);
  const isMissing    = currentStatuses.includes('missing');
  const hasRootCanal = currentStatuses.includes('root_canal')
    || currentRootZones.some((rz) => rz.conditions.includes('root_canal_treated'));

  // ── Surface condition picker ──────────────────────────────────────────────

  const renderSurfaceConditionPicker = () => {
    if (!isEditing || activeSurfaces.length === 0) return null;

    const allSurfaceConditions = activeSurfaces.map(
      (s) => getSurfaceConditions(currentSurfaces[s]),
    );
    const surfaceLabel = activeSurfaces.length === 1
      ? SURFACE_LABELS[activeSurfaces[0]]
      : activeSurfaces.map((s) => s[0].toUpperCase()).join('+');

    return (
      <div className="mt-3 rounded-xl border border-indigo-100 dark:border-indigo-900/50 bg-indigo-50/60 dark:bg-indigo-900/20 p-3">
        <div className="mb-2.5 flex items-center justify-between">
          <p className="text-xs font-semibold text-indigo-700 dark:text-indigo-300">
            {surfaceLabel}
          </p>
          {activeSurfaces.length > 1 && (
            <span className="rounded-full bg-indigo-100 dark:bg-indigo-800 px-2 py-0.5 text-[10px] font-bold text-indigo-600 dark:text-indigo-300">
              {activeSurfaces.length} flader
            </span>
          )}
        </div>
        {SURFACE_CATEGORIES.map((cat) => (
          <div key={cat.label} className="mb-2">
            <div className="mb-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-stone-400">
              {cat.icon}
              {cat.label}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {cat.keys.filter((k) => SURFACE_CONDITION_KEYS.includes(k)).map((c) => {
                const allHaveIt = allSurfaceConditions.every((conds) =>
                  c === 'healthy' ? conds.length === 0 : conds.includes(c),
                );
                const anyHasIt = allSurfaceConditions.some((conds) =>
                  c === 'healthy' ? conds.length === 0 : conds.includes(c),
                );
                return (
                  <ConditionChip
                    key={c}
                    {...CROWN_CONDITIONS[c]}
                    selected={allHaveIt}
                    partial={anyHasIt && !allHaveIt}
                    onClick={() => toggleSurfaceCondition(activeSurfaces, c)}
                  />
                );
              })}
            </div>
          </div>
        ))}
        <button
          type="button"
          onClick={() => setActiveSurfaces([])}
          className="mt-1 text-[10px] text-indigo-500 hover:text-indigo-700 underline"
        >
          Fravælg alle flader
        </button>
      </div>
    );
  };

  const renderRootConditionPicker = () => {
    if (!isEditing || !activeRootZone) return null;
    const zoneData = currentRootZones.find((rz) => rz.zone === activeRootZone);
    const current = zoneData?.conditions ?? [];
    return (
      <div className="mt-3 rounded-xl border border-amber-100 dark:border-amber-900/50 bg-amber-50/60 dark:bg-amber-900/20 p-3">
        <p className="mb-2 text-xs font-semibold text-amber-700 dark:text-amber-300">
          {ROOT_ZONE_LABELS[activeRootZone]}
        </p>
        <div className="flex flex-wrap gap-1.5">
          {ROOT_CONDITION_KEYS.map((c) => (
            <ConditionChip
              key={c}
              {...ROOT_CONDITIONS[c]}
              selected={c === 'healthy' ? current.length === 0 : current.includes(c)}
              onClick={() => toggleRootCondition(activeRootZone, c)}
            />
          ))}
        </div>
      </div>
    );
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full">

      {/* ── Sticky header ──────────────────────────────────────────────────── */}
      <div className="flex-shrink-0 flex items-center justify-between border-b border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 px-5 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-indigo-50 dark:bg-indigo-900/40">
            <span className="text-sm font-bold text-indigo-600 dark:text-indigo-300">{displayNumber}</span>
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-1.5">
              {currentStatuses.map((s) => {
                const meta = CROWN_CONDITIONS[s];
                if (!meta) return null;
                return (
                  <span
                    key={s}
                    className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-medium border"
                    style={{ background: meta.fill, borderColor: meta.stroke, color: meta.textColor }}
                  >
                    <span className="h-1.5 w-1.5 rounded-full flex-shrink-0" style={{ background: meta.stroke }} />
                    {meta.label}
                  </span>
                );
              })}
            </div>
            {tooth.last_examined_at && (
              <p className="mt-0.5 text-[10px] text-stone-400">
                Sidst undersøgt:{' '}
                <span className="font-medium text-stone-600 dark:text-stone-300">
                  {new Date(tooth.last_examined_at).toLocaleDateString('da-DK', {
                    day: 'numeric', month: 'long', year: 'numeric',
                  })}
                </span>
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {!readOnly && !isEditing && (
            <Button variant="primary" size="sm" onClick={startEditing}>
              <Pencil className="mr-1.5 h-3.5 w-3.5" />
              Rediger
            </Button>
          )}
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-1.5 text-stone-400 hover:bg-stone-100 dark:hover:bg-stone-700 hover:text-stone-600 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* ── Scrollable body ────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto bg-stone-50 dark:bg-stone-950 p-4 space-y-3">

        {/* Section 1: Flader */}
        {!isMissing && (
          <Section
            title="Flader"
            open={surfacesOpen}
            onToggle={() => setSurfacesOpen((v) => !v)}
            hint="klik én eller flere flader, vælg derefter tilstand"
          >
            <div className="md:grid md:grid-cols-[200px_1fr] md:gap-4 md:items-start">
              <div>
                <SurfaceDiagram
                  surfaces={currentSurfaces}
                  activeSurfaces={activeSurfaces}
                  onSurfaceClick={handleSurfaceClick}
                  isEditing={isEditing}
                />
              </div>
              <div>
                {isEditing && activeSurfaces.length === 0 && (
                  <p className="text-[11px] text-stone-400 mt-2 md:mt-0">
                    Klik en flade i diagrammet for at redigere
                  </p>
                )}
                {renderSurfaceConditionPicker()}
                {!isEditing && (
                  <div className="mt-2 space-y-1.5">
                    {(['occlusal', 'buccal', 'lingual', 'mesial', 'distal'] as Surface[]).map((surface) => {
                      const conditions = getSurfaceConditions(currentSurfaces[surface]);
                      if (conditions.length === 0) return null;
                      return (
                        <div key={surface} className="flex items-center justify-between gap-2 rounded-lg bg-white dark:bg-stone-800 border border-stone-100 px-3 py-2">
                          <span className="text-[11px] text-stone-500 dark:text-stone-400 whitespace-nowrap">
                            {SURFACE_LABELS[surface]}
                          </span>
                          <div className="flex flex-wrap gap-1 justify-end">
                            {conditions.map((c) => (
                              <span
                                key={c}
                                className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium border"
                                style={{
                                  background: CROWN_CONDITIONS[c]?.fill ?? '#f9f8f5',
                                  borderColor: CROWN_CONDITIONS[c]?.stroke ?? '#e7e5e4',
                                  color: CROWN_CONDITIONS[c]?.textColor ?? '#374151',
                                }}
                              >
                                {CROWN_CONDITIONS[c]?.label ?? c}
                              </span>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </Section>
        )}

        {/* Section 2: Rod */}
        {!isMissing && (
          <Section
            title="Rod"
            open={rootOpen}
            onToggle={() => setRootOpen((v) => !v)}
          >
            <div className="flex gap-4 items-start">
              <div className="flex-shrink-0 w-20">
                <RootDiagram
                  rootZones={currentRootZones}
                  activeZone={activeRootZone}
                  onZoneClick={handleRootZoneClick}
                  isEditing={isEditing}
                  isUpperTooth={isUpperTooth}
                  hasRootCanal={hasRootCanal}
                />
              </div>
              <div className="flex-1">
                {isEditing && !activeRootZone && (
                  <p className="text-[11px] text-stone-400">
                    Klik en rodzone for at redigere
                  </p>
                )}
                {renderRootConditionPicker()}
                {!isEditing && currentRootZones.length > 0 && (
                  <div className="space-y-1.5">
                    {currentRootZones.map((rz) => (
                      <div key={rz.zone} className="flex items-center justify-between gap-2 rounded-lg bg-white border border-stone-100 px-3 py-2">
                        <span className="text-[11px] text-stone-500 whitespace-nowrap">
                          {ROOT_ZONE_LABELS[rz.zone]}
                        </span>
                        <div className="flex flex-wrap gap-1 justify-end">
                          {rz.conditions.map((c) => (
                            <span
                              key={c}
                              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium border"
                              style={{
                                background: ROOT_CONDITIONS[c]?.fill ?? '#f9f8f5',
                                borderColor: ROOT_CONDITIONS[c]?.stroke ?? '#e7e5e4',
                                color: ROOT_CONDITIONS[c]?.textColor ?? '#374151',
                              }}
                            >
                              {ROOT_CONDITIONS[c]?.label ?? c}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </Section>
        )}

        {/* Section 3: Tandstatus */}
        <Section
          title="Tandstatus"
          open={statusOpen}
          onToggle={() => setStatusOpen((v) => !v)}
        >
          <div className="space-y-3">
            {TANDSTATUS_CATEGORIES.map((cat) => {
              const visibleKeys = cat.keys.filter((c) => {
                if (isEditing) return true;
                const isSelected = c === 'healthy'
                  ? currentStatuses.length === 0 || (currentStatuses.length === 1 && currentStatuses[0] === 'healthy')
                  : currentStatuses.includes(c);
                return isSelected;
              });
              if (!isEditing && visibleKeys.length === 0) return null;
              return (
                <div key={cat.label}>
                  <div className="mb-1.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-stone-400">
                    {cat.icon}
                    {cat.label}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {cat.keys.map((c) => {
                      const isSelected = c === 'healthy'
                        ? currentStatuses.length === 0 || (currentStatuses.length === 1 && currentStatuses[0] === 'healthy')
                        : currentStatuses.includes(c);
                      if (!isEditing && !isSelected) return null;
                      return (
                        <ConditionChip
                          key={c}
                          {...CROWN_CONDITIONS[c]}
                          selected={isSelected}
                          onClick={() => isEditing && toggleToothCondition(c)}
                        />
                      );
                    })}
                  </div>
                </div>
              );
            })}
            {!isEditing && currentStatuses.length === 0 && (
              <p className="text-[11px] text-stone-400 italic">Ingen status registreret</p>
            )}
          </div>
        </Section>

        {/* Section 4: Noter */}
        <Section
          title="Noter"
          icon={<FileText className="h-3.5 w-3.5" />}
          open={notesOpen}
          onToggle={() => setNotesOpen((v) => !v)}
        >
          {isEditing ? (
            <Textarea
              value={editedNotes}
              onChange={(e) => setEditedNotes(e.target.value)}
              rows={4}
              placeholder="Kliniske noter om denne tand..."
              className="text-sm"
            />
          ) : (
            <p className="rounded-xl bg-white dark:bg-stone-800 border border-stone-100 px-3 py-2.5 text-sm text-stone-700 dark:text-stone-300 min-h-[72px]">
              {tooth.notes || (
                <span className="text-stone-400 italic">Ingen noter</span>
              )}
            </p>
          )}
        </Section>
      </div>

      {/* ── Sticky footer (edit mode only) ────────────────────────────────── */}
      {isEditing && (
        <div className="flex-shrink-0 flex items-center justify-end gap-2 border-t border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 px-5 py-3">
          <Button variant="secondary" size="sm" onClick={cancelEditing}>
            {t('common.cancel')}
          </Button>
          <Button size="sm" onClick={handleSave}>
            <Save className="mr-1.5 h-3.5 w-3.5" />
            {t('common.save')}
          </Button>
        </div>
      )}
    </div>
  );
}
